import { useEffect, useState } from 'react'

import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router-dom'
import Modal from 'react-modal'
import { useDispatch, useSelector } from 'react-redux'

import { useQueryClient } from '@tanstack/react-query'
import {
  BottomActionBarWrapper,
  BottomActionBar,
} from '../../components/BottomActionBar'
import Button from '../../components/Button'
import { Row, RowWrapper } from '../../components/Row'
import TextInput from '../../components/TextInput'
import Title from '../../components/Title'
import { reset } from '../../store/actions/drawer.actions'
import {
  changePassword,
  checkPasswordSecurity,
  checkUser,
} from '../../store/actions/user.actions'
import styles from './ResetPasswordModal.module.scss'
import { VALIDATION_MSG } from 'utils/constants'
import { USER_KEYS } from 'hooks/user'
import { TYPES_ALERTS } from 'utils/constants/global'
import { useSnackBarFeedback } from 'context/snackbarFeedback'
import { ROUTES } from 'routes/paths'

const modalFullStyles = {
  content: {
    border: '0',
    borderRadius: '0',
    bottom: '0',
    left: '0',
    padding: '0',
    right: '0',
    top: '0',
  },
  overlay: {
    zIndex: 12,
  },
}

const ResetPasswordModal = () => {
  const queryClient = useQueryClient()
  const { t } = useTranslation()
  const { push } = useHistory()
  const { loader, request } = useSelector(({ drawer }) => drawer)
  const { userFlags } = queryClient.getQueryData({
    queryKey: [USER_KEYS.GET_USER],
  })
  const [isMatch, setIsMatch] = useState(null)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [newPassword, setNewPassword] = useState('')
  const [passwordConfirm, setPasswordConfirm] = useState('')
  const dispatch = useDispatch()
  const addFeedback = useSnackBarFeedback()

  const isValid = request?.secure
  const openModal = request?.secure
  const validationCode = request?.code

  useEffect(() => {
    const resetPassword = userFlags?.FORCE_RESET_PASSWORD === 'true'
    const forceResetPassword = userFlags?.FORCE_RESET_PASSWORD === 'true'
    if (forceResetPassword) {
      push(ROUTES.RESET_PASSWORD)
    } else {
      if (resetPassword !== openModal) {
        setIsModalOpen(resetPassword)
      }
    }
  }, [userFlags])

  useEffect(() => {
    if (passwordConfirm) {
      setIsMatch(passwordConfirm === newPassword)
    }
  }, [passwordConfirm, newPassword])

  const checkPassword = async (password) => {
    dispatch(reset('request'))
    dispatch(checkPasswordSecurity(password))
  }

  const handleChangePassword = (value) => {
    if (!value) {
      dispatch(reset('request'))
    } else if (newPassword !== value) {
      checkPassword(value)
    }
    setNewPassword(value)
  }

  const handleChangeRepeatPassword = (value) => {
    const newPasswordConfirm = value || passwordConfirm
    setPasswordConfirm(newPasswordConfirm)
  }

  const setModalOpen = (isOpen) => {
    setIsModalOpen(isOpen)
  }

  const setPassword = async () => {
    try {
      dispatch(changePassword(newPassword, passwordConfirm))
      dispatch(checkUser())
      addFeedback({
        dataTestId: 'success-feedback-delete-classroom',
        id: 'success-feedback-delete-classroom',
        text: t('Los cambios se han realizado con éxito'),
        variant: TYPES_ALERTS.SUCCESS,
      })
    } catch (error) {
      addFeedback({
        dataTestId: 'error-feedback-delete-classroom',
        id: 'error-feedback-delete-classroom',
        text: t('Ha ocurrido un problema'),
        variant: TYPES_ALERTS.ERROR,
      })
    }

    setModalOpen(false)
  }

  return (
    isModalOpen && (
      <Modal isOpen={isModalOpen} style={modalFullStyles}>
        <BottomActionBarWrapper>
          <section className={styles.main}>
            <div className={styles.container}>
              <Title
                subtitle={t(
                  'Esta contraseña es personal y te dará acceso a tu cuenta de Edubook'
                )}
                title={t('Antes que nada, por favor, define tu contraseña')}
              />
              <div className={styles.form}>
                <RowWrapper>
                  <Row>
                    <TextInput
                      invalidMessage={t(VALIDATION_MSG[validationCode])}
                      isValid={isValid}
                      label={t('Introduce tu nueva contraseña')}
                      name='password'
                      onBlur={(value) => handleChangePassword(value)}
                      onChange={handleChangePassword}
                      showLoader={!!loader}
                      showPasswordLabel={t('Mostrar')}
                      type='password'
                      value={newPassword}
                    />
                  </Row>
                  <Row>
                    <TextInput
                      invalidMessage={t(
                        'Las contraseñas introducidas no coinciden'
                      )}
                      isValid={isMatch}
                      label={t('Vuelve a escribir la contraseña')}
                      name='passwordConfirm'
                      onBlur={(value) => handleChangeRepeatPassword(value)}
                      onChange={handleChangeRepeatPassword}
                      showPasswordLabel={t('Mostrar')}
                      type='password'
                      value={passwordConfirm}
                    />
                  </Row>
                </RowWrapper>
              </div>
              <BottomActionBar>
                <Button
                  classes={{ large: true }}
                  disabled={!(isValid && isMatch)}
                  onPress={setPassword}
                >
                  {t('common.actions.continue', { keySeparator: '.' })}
                </Button>
              </BottomActionBar>
            </div>
          </section>
        </BottomActionBarWrapper>
      </Modal>
    )
  )
}

export default ResetPasswordModal
